import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from 'prop-types';
import {
    getTrainingById,
    hasUserPurchasedTraining,
    purchaseTraining,
    getContentTypeLabel,
    getCategoryLabel,
    getContentTypeIcon,
    getCategoryIcon,
    getContentUrl,
    CONTENT_TYPES
} from "../../../services/trainingService";
import { auth, firestore } from "../../../config/firebase";
import firebase from "firebase/app";
import { Document, Page, pdfjs } from 'react-pdf';

// Configuration pour react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const TrainingDetailPage = ({ user, history, match }) => {
    const { id } = match.params;

    // États
    const [training, setTraining] = useState(null);
    const [isPurchased, setIsPurchased] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isProcessingFreeAccess, setIsProcessingFreeAccess] = useState(false);

    // État spécifique pour l'affichage des PDF
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    // Vérifier si la formation est gratuite
    const isFreeTraining = training && training.price === 0;

    // Chargement des données de la formation
    useEffect(() => {
        const loadTrainingData = async () => {
            setIsLoading(true);
            try {
                // Vérifier l'utilisateur connecté
                const currentUser = user?.uid ? user : auth.currentUser;

                if (!currentUser) {
                    setError("Vous devez être connecté pour accéder à cette formation");
                    setIsLoading(false);
                    return;
                }

                // Charger les détails de la formation
                const trainingData = await getTrainingById(id);
                if (!trainingData) {
                    setError("Formation introuvable");
                    setIsLoading(false);
                    return;
                }

                setTraining(trainingData);

                // Vérifier si l'utilisateur a acheté cette formation
                const purchased = await hasUserPurchasedTraining(currentUser.uid, id);
                setIsPurchased(purchased);

                // Si formation gratuite et non achetée, ne pas rediriger
                const isFree = trainingData.price === 0;

                // Ne rediriger que si la formation est payante et non achetée
                if (!purchased && !isFree) {
                    history.replace(`/app/training/purchase/${id}`);
                }

            } catch (err) {
                setError("Impossible de charger les détails de la formation. Veuillez réessayer.");
            } finally {
                setIsLoading(false);
            }
        };

        loadTrainingData();
    }, [id, user, history]);

    // Accéder gratuitement à une formation
    const handleFreeAccess = async () => {
        if (!training || !user) return;

        setIsProcessingFreeAccess(true);
        try {
            const currentUser = user?.uid ? user : auth.currentUser;

            if (!currentUser?.uid) {
                throw new Error("Vous devez être connecté pour accéder à cette formation");
            }

            // Enregistrer l'accès gratuit dans la base de données
            const userTrainingRef = firestore.collection('userTrainings').doc();
            await userTrainingRef.set({
                userId: currentUser.uid,
                trainingId: training.id,
                trainingTitle: training.title,
                trainingCategory: training.category,
                trainingContentType: training.contentType,
                trainingSummary: training.summary,
                trainingFileUrl: training.fileUrl,
                trainingFileName: training.fileName,
                trainingYoutubeUrl: training.youtubeUrl,
                purchaseDate: firebase.firestore.FieldValue.serverTimestamp(),
                price: 0,
                status: 'completed',
                isFree: true,
                createdAt: firebase.firestore.FieldValue.serverTimestamp()
            });

            // Mettre à jour l'état
            setIsPurchased(true);

        } catch (error) {
            setError(`Erreur lors de l'accès: ${error.message}`);
        } finally {
            setIsProcessingFreeAccess(false);
        }
    };

    // Gestion des documents PDF
    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const changePage = (offset) => {
        setPageNumber(prevPageNumber => prevPageNumber + offset);
    };

    const previousPage = () => changePage(-1);
    const nextPage = () => changePage(1);

    // Téléchargement du contenu
    const handleDownload = () => {
        if (!training || !training.fileUrl) {
            setError("Aucun fichier disponible pour le téléchargement");
            return;
        }

        if (!isPurchased) {
            setError("Vous devez d'abord acquérir cette formation pour la télécharger");
            return;
        }

        try {
            // Créer un lien temporaire et simuler un clic pour télécharger
            const link = document.createElement('a');
            link.href = training.fileUrl;
            link.setAttribute('download', training.fileName || `formation_${training.title || 'document'}.pdf`);
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setError("Erreur lors du téléchargement. Veuillez réessayer.");
        }
    };

    // Formatage du prix
    const formatPrice = (price) => {
        if (price === 0) {
            return "Gratuit";
        }
        // Format personnalisé pour afficher "1000 FCFA" au lieu de "1000F"
        return `${new Intl.NumberFormat("fr-FR").format(price)} FCFA`;
    };

    // Retour à la liste des formations
    const handleBackToList = () => {
        history.push('/app/training');
    };

    // Rendu du contenu en fonction du type
    const renderContent = () => {
        if (!training || !isPurchased) return null;

        switch (training.contentType) {
            case CONTENT_TYPES.PDF:
                return (
                    <div className="pdf-viewer">
                        <div className="pdf-controls">
                            <button
                                disabled={pageNumber <= 1}
                                onClick={previousPage}
                                className="btn btn-sm btn-default"
                            >
                                <i className="pe-7s-angle-left"></i> Précédent
                            </button>
                            <span className="page-info">
                Page {pageNumber} sur {numPages || '--'}
              </span>
                            <button
                                disabled={pageNumber >= numPages}
                                onClick={nextPage}
                                className="btn btn-sm btn-default"
                            >
                                Suivant <i className="pe-7s-angle-right"></i>
                            </button>
                        </div>

                        <Document
                            file={training.fileUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={<div className="loading-pdf">Chargement du document...</div>}
                            error={<div className="error-pdf">Impossible de charger le document</div>}
                        >
                            <Page
                                pageNumber={pageNumber}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                className="pdf-page"
                            />
                        </Document>
                    </div>
                );

            case CONTENT_TYPES.VIDEO:
                return (
                    <div className="video-player">
                        <video controls width="100%">
                            <source src={training.fileUrl} type="video/mp4" />
                            Votre navigateur ne prend pas en charge la lecture vidéo.
                        </video>
                    </div>
                );

            case CONTENT_TYPES.BOOK:
            default:
                return (
                    <div className="book-viewer">
                        <div className="book-info">
                            <i className="pe-7s-notebook book-icon"></i>
                            <h3>Livre numérique</h3>
                            <p>Téléchargez ce livre pour le consulter sur votre appareil.</p>
                            <button
                                onClick={handleDownload}
                                className="btn btn-primary btn-fill"
                            >
                                <i className="pe-7s-download"></i> Télécharger le livre
                            </button>
                        </div>
                    </div>
                );
        }
    };

    // Rendu du bouton d'accès pour les formations non achetées
    const renderAccessButton = () => {
        if (isPurchased) return null;

        if (isFreeTraining) {
            return (
                <div className="free-access-section">
                    <div className="free-access-info">
                        <i className="pe-7s-gift"></i>
                        <h3>Formation Gratuite</h3>
                        <p>Accédez immédiatement à cette formation sans aucun paiement</p>
                        <button
                            onClick={handleFreeAccess}
                            className="btn btn-info btn-fill btn-lg"
                            disabled={isProcessingFreeAccess}
                        >
                            {isProcessingFreeAccess ? (
                                <>
                                    <div className="button-spinner"></div>
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <i className="pe-7s-download"></i> Accéder gratuitement
                                </>
                            )}
                        </button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="purchase-required">
                    <i className="pe-7s-lock"></i>
                    <h3>Contenu verrouillé</h3>
                    <p>Vous devez acheter cette formation pour accéder à son contenu.</p>
                    <button
                        onClick={() => history.push(`/app/training/purchase/${id}`)}
                        className="btn btn-primary btn-fill"
                    >
                        <i className="pe-7s-cart"></i> Acquérir cette formation
                    </button>
                </div>
            );
        }
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Chargement du contenu...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="alert alert-danger">{error}</div>
                <button
                    onClick={handleBackToList}
                    className="btn btn-default"
                >
                    <i className="pe-7s-back"></i> Retour aux formations
                </button>
            </div>
        );
    }

    if (!training) {
        return (
            <div className="error-container">
                <div className="alert alert-warning">Formation introuvable</div>
                <button
                    onClick={handleBackToList}
                    className="btn btn-default"
                >
                    <i className="pe-7s-back"></i> Retour aux formations
                </button>
            </div>
        );
    }

    return (
        <div className="training-detail-page">
            <div className="navigation-bar">
                <button
                    onClick={handleBackToList}
                    className="btn btn-default btn-sm back-button"
                >
                    <i className="pe-7s-back"></i> Retour aux formations
                </button>

                {isPurchased && (
                    <button
                        onClick={handleDownload}
                        className="btn btn-success btn-sm download-button"
                    >
                        <i className="pe-7s-download"></i> Télécharger
                    </button>
                )}
            </div>

            <div className="training-header">
                <div className="training-header-content">
                    <div className="training-type-info">
                        <i className={getContentTypeIcon(training.contentType)}></i>
                        <span>{getContentTypeLabel(training.contentType)}</span>
                        {isFreeTraining && (
                            <span className="free-badge">
                    <i className="pe-7s-gift"></i>
                    Gratuit
                  </span>
                        )}
                    </div>

                    <h1 className="training-title">{training.title}</h1>

                    <div className="training-meta">
                        <div className="category">
                            <i className={getCategoryIcon(training.category)}></i>
                            <span>{getCategoryLabel(training.category)}</span>
                        </div>

                        <div className={`price ${isFreeTraining ? 'free-price' : ''}`}>
                            {formatPrice(training.price)}
                        </div>
                    </div>

                    <div className="training-description">
                        <h3>Résumé</h3>
                        <p>{training.summary}</p>
                    </div>
                </div>
            </div>

            <div className="training-content-wrapper">
                {isPurchased ? (
                    <div className="purchased-content">
                        <div className="download-section">
                            <div className="success-badge">
                                <i className="pe-7s-check"></i>
                                <span>Formation acquise avec succès !</span>
                            </div>
                            <button
                                onClick={handleDownload}
                                className="btn btn-success btn-fill btn-lg main-download-button"
                            >
                                <i className="pe-7s-download"></i> Télécharger la formation
                            </button>
                            <p className="download-info">
                                <i className="pe-7s-info"></i> Le téléchargement commencera immédiatement
                            </p>
                        </div>
                        {renderContent()}
                    </div>
                ) : (
                    renderAccessButton()
                )}
            </div>

            <style jsx>{`
                .training-detail-page {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    font-family: 'Roboto', sans-serif;
                }

                .navigation-bar {
                    display: flex;
                    justify-content: space-between;
                    padding: 15px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #e9ecef;
                }

                .training-header {
                    padding: 30px;
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                }

                .training-header-content {
                    max-width: 1000px;
                    margin: 0 auto;
                }

                .training-type-info {
                    display: flex;
                    align-items: center;
                    margin-bottom: 10px;
                    gap: 15px;
                }

                .training-type-info i {
                    font-size: 1.5rem;
                    color: #4285f4;
                }

                .free-badge {
                    background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
                    color: white;
                    padding: 5px 12px;
                    border-radius: 15px;
                    font-size: 0.8rem;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }

                .training-title {
                    font-size: 2rem;
                    margin-bottom: 15px;
                    color: #333;
                }

                .training-meta {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .category {
                    display: flex;
                    align-items: center;
                    background: white;
                    padding: 8px 15px;
                    border-radius: 20px;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
                }

                .category i {
                    margin-right: 8px;
                    color: #4285f4;
                }

                .price {
                    background: #4caf50;
                    color: white;
                    padding: 8px 15px;
                    border-radius: 20px;
                    font-weight: 600;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
                }

                .free-price {
                    background: #2196f3;
                }

                .training-description {
                    background: white;
                    padding: 20px;
                    border-radius: 10px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                }

                .training-description h3 {
                    margin-top: 0;
                    color: #333;
                    font-size: 1.3rem;
                    margin-bottom: 10px;
                }

                .training-description p {
                    font-size: 1.1rem;
                    line-height: 1.7;
                    color: #555;
                }

                .training-content-wrapper {
                    flex: 1;
                    padding: 30px;
                    background: #f8f9fa;
                    display: flex;
                    justify-content: center;
                    align-items: flex-start;
                    overflow: auto;
                }

                .purchased-content {
                    width: 100%;
                    max-width: 1000px;
                }

                .download-section {
                    background: white;
                    padding: 30px;
                    border-radius: 10px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    text-align: center;
                    margin-bottom: 30px;
                    border: 2px solid #4caf50;
                }

                .success-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
                    color: white;
                    padding: 12px 24px;
                    border-radius: 25px;
                    font-size: 1.1rem;
                    font-weight: 600;
                    margin-bottom: 20px;
                    box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
                }

                .success-badge i {
                    font-size: 1.5rem;
                }

                .main-download-button {
                    font-size: 1.2rem;
                    padding: 15px 40px;
                    margin: 10px 0;
                    border-radius: 8px;
                    box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
                    transition: all 0.3s ease;
                }

                .main-download-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
                }

                .main-download-button i {
                    margin-right: 10px;
                    font-size: 1.3rem;
                }

                .download-info {
                    color: #666;
                    font-size: 0.9rem;
                    margin-top: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }

                .download-info i {
                    color: #2196f3;
                }

                .pdf-viewer {
                    background: white;
                    padding: 20px;
                    border-radius: 10px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    width: 100%;
                    max-width: 1000px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .pdf-controls {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                    max-width: 400px;
                    margin-bottom: 20px;
                }

                .page-info {
                    font-size: 0.9rem;
                    color: #666;
                }

                .pdf-page {
                    border: 1px solid #e0e0e0;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
                }

                .loading-pdf, .error-pdf {
                    padding: 40px;
                    text-align: center;
                    color: #666;
                }

                .video-player {
                    width: 100%;
                    max-width: 1000px;
                    background: black;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
                    border-radius: 10px;
                    overflow: hidden;
                }

                .book-viewer {
                    width: 100%;
                    max-width: 600px;
                }

                .book-info {
                    background: white;
                    padding: 40px;
                    text-align: center;
                    border-radius: 10px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                }

                .book-icon {
                    font-size: 4rem;
                    color: #4285f4;
                    margin-bottom: 20px;
                }

                .free-access-section {
                    width: 100%;
                    max-width: 500px;
                }

                .free-access-info {
                    background: white;
                    padding: 40px;
                    text-align: center;
                    border-radius: 10px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    border: 2px solid #2196f3;
                }

                .free-access-info i {
                    font-size: 4rem;
                    color: #2196f3;
                    margin-bottom: 20px;
                }

                .free-access-info h3 {
                    color: #2196f3;
                    margin-bottom: 10px;
                }

                .purchase-required {
                    background: white;
                    padding: 40px;
                    text-align: center;
                    border-radius: 10px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    max-width: 500px;
                }

                .purchase-required i {
                    font-size: 4rem;
                    color: #f44336;
                    margin-bottom: 20px;
                }

                .button-spinner {
                    width: 20px;
                    height: 20px;
                    border: 3px solid rgba(255,255,255,0.3);
                    border-radius: 50%;
                    border-top-color: white;
                    animation: spin 1s linear infinite;
                    margin-right: 10px;
                }

                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    padding: 40px;
                }

                .spinner {
                    border: 4px solid rgba(0, 0, 0, 0.1);
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    border-left-color: #4285f4;
                    animation: spin 1s linear infinite;
                    margin-bottom: 20px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .error-container {
                    padding: 40px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .error-container .alert {
                    margin-bottom: 20px;
                    width: 100%;
                    max-width: 500px;
                }
            `}</style>
        </div>
    );
};

TrainingDetailPage.propTypes = {
    user: PropTypes.shape({
        uid: PropTypes.string,
        email: PropTypes.string,
        displayName: PropTypes.string
    }),
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired
};

export default withRouter(TrainingDetailPage);